import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { AcquaintanceTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/AcquaintanceTypeCodesEnum';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCatalogsPmProfActTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmActTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActTypeSelectOption';
import { ActTypesEnum } from 'KnmErknmPackage/ErknmActsModule/enums';
import {
  getKnmFormRef,
  getKnmIsErknmStatusTypeCompleted,
  isKnmStartDateBeforeUserDate,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getKnmPmInitialFormValues } from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { PM_ACT_BLOCK_ID } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { IKnmPmFormRequirementsNpa } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import { PmPaperlessAct } from './PmPaperlessAct';
import { PmActItemStatus } from './PmActItemStatus';
import { checkHasErrorInCollapseBlock } from '../../../../../functions/checkHasErrorInCollapseBlock';
import { IKnmErknmFormObject } from '../../../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';

const COLLAPSE_TITLE_BASE = 'Акт';

interface IPmActItemProps {
  isWatchOnly: boolean;
  name: string;
  onRemove: () => void;
  value: IKnmPmFormAct;
  values: IKnmPmForm;
  requirementsNpa: IKnmPmFormRequirementsNpa[] | undefined;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  objectsPm?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
  isDisabledByDirty?: boolean;
}

interface IPmActItemStoreProps {
  formRef: IKnmFormRef | undefined;
  initialFormValues: IKnmPmForm;
  isKnmStatusCompleted: boolean;
  isStartDateBeforeUserDate?: boolean;
  pmProfActTypesOptions: IKnmActTypeSelectOption[] | undefined;
}

const PmActItemWithoutStore = ({
  isWatchOnly,
  name,
  onRemove,
  value,
  formRef,
  initialFormValues,
  isKnmStatusCompleted,
  isStartDateBeforeUserDate,
  pmProfActTypesOptions,
  values,
  requirementsNpa,
  knoOrganization,
  supervisionType,
  objectsPm,
  riskIndicators,
  isDisabledByDirty = false,
}: IPmActItemProps & IPmActItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const { isNew: isNewAct } = value;
  const [knmActType, setKnmActTypeSelected] = useState<number | null>(null);

  const initActValues = initialFormValues.act;
  const isActFilled =
    !!initActValues?.numberAct &&
    !!initActValues.creationActDateTime &&
    !!initActValues.holdingDateTime &&
    !!initActValues.document?.attachments?.length &&
    !!initActValues.fioSigner &&
    !!initActValues.titleSigner &&
    !!initActValues.knoInspectors?.length &&
    (!initActValues.isViolationResolved?.value || !!initActValues.isViolationResolvedNote) &&
    (!!initActValues.durationDays || !!initActValues.durationHours) &&
    !!initActValues.acquaintanceType &&
    !!initActValues.events?.length &&
    (initActValues.acquaintanceType?.code === AcquaintanceTypeCodesEnum.ACQ_TYPE_II ||
      initActValues.acquaintanceType?.code === AcquaintanceTypeCodesEnum.ACQ_TYPE_IV ||
      (!!initActValues.fioReader && !!initActValues.titleReader));

  const isDisabledByComplete =
    !isNewAct && isKnmStatusCompleted && (knmActType === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE || isActFilled);

  useEffect(() => {
    if (value.typeAct) {
      setKnmActTypeSelected(value.typeAct.id);
    }
  }, [value]);

  const [isTypeActChanged, setTypeActChanged] = useState(false);

  const handleActTypeChange = useCallback(
    (v: ISelectOption) => {
      // if (knmActType !== null) { //TODO зачем нужна эта проверка?
      setTypeActChanged(true);
      const isNew = formRef?.current.getState().values.act?.isNew ?? false;
      const numberAct = formRef?.current.getState().values.act?.numberAct;
      const typeAct = pmProfActTypesOptions?.find(option => option.id === v?.value);

      const uuid = uuidv4();

      formRef?.current.change('act', {
        involvedExperts: undefined,
        extractVersion: undefined,
        involvedWitnesses: undefined,
        acquaintanceLackNoInfoComment: undefined,
        acquaintanceLackType: undefined,
        acquaintanceType: undefined,
        canceledReason: undefined,
        canceledReasonDescription: undefined,
        created: undefined,
        createdBy: undefined,
        creationActDateTime: undefined,
        document: undefined,
        durationDays: undefined,
        durationHours: undefined,
        events: undefined,
        fioAndPositionSinger: undefined,
        fioReader: undefined,
        fioSigner: undefined,
        holdingDateTime: undefined,
        isNew,
        isViolatedMandatoryRequirements: undefined,
        isViolationResolved: undefined,
        isViolationResolvedNote: undefined,
        knoInspectors: undefined,
        listId: uuid,
        guid: uuid,
        nextWordDayActDateTime: undefined,
        noteInvalidate: undefined,
        numberAct,
        organization: undefined,
        refuserFio: undefined,
        refuserTitle: undefined,
        canceled: false,
        titleReader: undefined,
        titleSigner: undefined,
        typeAct,
        updated: undefined,
        updatedBy: undefined,
        violatedMandatoryRequirements: undefined,
        familiarizationNotifications: undefined,
        remarks: undefined,
        signed: undefined,
        signActDateTime: undefined,
        paperless: true,
        firstExtractDateTime: null,
        systemSignatureUsed: false,
        systemSignatureTitle: null,
        systemSignatureDateTime: null,
        systemSignatureMnemonic: null,
        signers: undefined,
      });
      // }

      if (v?.id === ActTypesEnum.PM_PROF_ACT) {
        // @ts-ignore
        formRef?.current.change(`${name}.organization`, {
          subjectTypePm: values?.subjectTypePm,
          inn: values?.inn,
          ogrn: values?.ogrn,
          organizationName: values?.organizationName,
          isFiz: values?.isFiz,
          kpp: values?.kpp,
          rafpNza: values?.rafpNza,
          country: values?.country,
          nkoForeignAgentsRegistryNumber: values?.nkoForeignAgentsRegistryNumber,
          nkoForeignAgentsRegistryInclusionDate: values?.nkoForeignAgentsRegistryInclusionDate,
          nkoForeignAgentsRegistryExclusionDate: values?.nkoForeignAgentsRegistryExclusionDate,
          isNko: values?.nkoForeignAgentsRegistryInclusionDate,
          locationAddress: values?.locationAddress,
          workAddress: values?.workAddress,
          factualAddress: values?.factualAddress,
          snils: values?.snils,
          document: values?.document,
          isSonko: values?.isSonko,
          mspCode: values?.mspCode,
          isPresentInItRegistry: values?.isPresentInItRegistry,
          itAccreditationResponse: values?.itAccreditationResponse,
          smevItAccreditationRequestStatus: values?.smevItAccreditationRequestStatus,
          checkSubjectResult: values?.checkSubjectResult,
        });
      }

      setKnmActTypeSelected(v?.value);
    },
    [formRef, knmActType, pmProfActTypesOptions, name, values],
  );

  const options = pmProfActTypesOptions?.filter(option => {
    // 3 - Акт ПМ доступен для всех статусов, если уже дата начала наступила
    // 4 - Акт о невозможности проведения ПМ, доступен для всех статусов, кроме "Завершено"
    return option.id === 3 ? !isStartDateBeforeUserDate : !isKnmStatusCompleted;
  });

  const [actTypeOptions, setActTypeOptions] = useState<IKnmActTypeSelectOption[] | undefined>(
    options ? [...options] : undefined,
  );

  useEffect(() => {
    // @ts-ignore
    if (formRef?.current.getFieldState('act')?.value?.typeAct?.id === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE) {
      const typeOptions = options?.filter(x => {
        return x.id === 3;
      });

      setActTypeOptions(typeOptions ? [...typeOptions] : undefined);
    } else {
      setActTypeOptions(options ? [...options] : undefined);
    }
  }, [formRef?.current.getFieldState('act')]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  return (
    <KnmCollapse
      theme="fields"
      title={COLLAPSE_TITLE_BASE}
      id={PM_ACT_BLOCK_ID}
      subtitle={value.numberAct}
      opened={(isNewAct || !isTypeActChanged) && !value?.fileInvalidate}
      onClose={isNewAct ? onRemove : undefined}
      headerPostfix={<PmActItemStatus act={value} />}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({ label: 'Вид акта', field: `${name}.typeAct`, isPm: true })}
            id={`${PM_ACT_BLOCK_ID}.typeAct`}
          >
            <SelectInputField
              name={`${name}.typeAct`}
              options={actTypeOptions}
              size="medium"
              disabled={
                isWatchOnly ||
                isDisabledByComplete ||
                value.signed ||
                !!value.fileInvalidate ||
                isDisabledByDirty ||
                !!value.document
              }
              onChange={handleActTypeChange}
              isClearable={false}
            />
          </FieldBlock>
        </Row>

        {(value.typeAct?.id === ActTypesEnum.PM_PROF_ACT ||
          value.typeAct?.id === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE) && (
          <PmPaperlessAct
            value={value}
            isWatchOnly={isWatchOnly}
            isDisabledField={isWatchOnly || isDisabledByComplete || !!value.signed || !!value.fileInvalidate}
            isDisabledByDirty={isDisabledByDirty}
            name={name}
            inspectorsErknm={values.inspectorsErknm}
            experts={values.experts}
            requirementsNpa={requirementsNpa}
            knoOrganization={knoOrganization}
            supervisionType={supervisionType}
            objectsPm={objectsPm}
            riskIndicators={riskIndicators}
          />
        )}
      </>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IPmActItemStoreProps => ({
  formRef: getKnmFormRef(state),
  initialFormValues: getKnmPmInitialFormValues(state),
  isKnmStatusCompleted: getKnmIsErknmStatusTypeCompleted(state),
  pmProfActTypesOptions: getCatalogsPmProfActTypesSelectOptions(state),
  isStartDateBeforeUserDate: isKnmStartDateBeforeUserDate(state),
});

export const PmActItem = connect(mapStateToProps)(PmActItemWithoutStore);
